/**
 * src/lib/voice/properNounCorrect.js — TICKET-076
 *
 * Client-side conservative proper-noun correction for the LIVE streaming-STT
 * final. gpt-realtime-whisper mis-spells ACN proper nouns (project / vessel /
 * deal names) differently each turn — "Muluc" -> "Muruk", a new project "Yaiza"
 * lands raw. The existing client pass (useVoiceConversation: normalizeTranscriptSync
 * over domain_vocabulary.misheard_as) only catches names a human already curated;
 * a brand-new project name is invisible to every map. This module corrects the
 * streamed final against the user's LIVE entity names (which the hook already
 * holds from AppDataContext) so no pre-curated mishearing is required.
 *
 * This is a faithful client port of the box-local matcher
 * (services/stt-proxy/vocabCorrect.js correctFinalSync, names branch). Keep the
 * two in sync — same phonetic classes, same guards. Strategy is precision over
 * recall because it AUTO-edits the transcript the CEO sees + we persist:
 *   phonetic-key EQUAL  AND  Levenshtein <= maxEditsFor(token)  AND  |len delta| <= 2
 *   AND token not a common EN/ES word  AND token length >= 4.
 * That catches close mishearings (muruk->Muluc, bimony->Bimini) while leaving
 * ordinary speech untouched ("milk": key matches Muluc but Levenshtein 3 -> kept).
 * TICKET-526: that budget is 1 for tokens of 4 characters or fewer -- a flat 2
 * rewrote HALF of every short word in production (`less~>Luis`, `nice~>Mike`).
 *
 * Pure + synchronous + zero I/O — safe on the voice hot path. Never throws.
 */

// Common EN/ES words that must NEVER be rewritten even if phonetically near a
// name. Mirrors services/stt-proxy/vocabCorrect.js COMMON_WORDS (the set that
// fixed the 2026-06-07 dictionary-pollution incident). Keep in sync.
const COMMON_WORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'but', 'if', 'then', 'than', 'so', 'as', 'at',
  'by', 'for', 'from', 'in', 'into', 'of', 'off', 'on', 'onto', 'out', 'over',
  'to', 'up', 'with', 'without', 'about', 'after', 'before', 'between', 'under',
  'again', 'once', 'i', 'me', 'my', 'we', 'us', 'our', 'you', 'your', 'he',
  'him', 'his', 'she', 'her', 'it', 'its', 'they', 'them', 'their', 'this',
  'that', 'these', 'those', 'who', 'whom', 'which', 'what', 'when', 'where',
  'why', 'how', 'here', 'there', 'is', 'am', 'are', 'was', 'were', 'be', 'been',
  'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'can',
  'could', 'shall', 'should', 'may', 'might', 'must', 'not', 'no', 'yes',
  'need', 'want', 'like', 'know', 'think', 'make', 'made', 'take', 'give',
  'get', 'got', 'going', 'come', 'came', 'see', 'say', 'said', 'tell', 'put',
  'set', 'let', 'keep', 'find', 'feel', 'look', 'use', 'work', 'call', 'ask',
  'try', 'leave', 'move', 'turn', 'start', 'stop', 'show', 'hear', 'hold',
  'bring', 'mean', 'talk', 'today', 'tomorrow', 'now', 'just', 'also', 'very',
  'more', 'most', 'some', 'any', 'all', 'each', 'every', 'both', 'many', 'much',
  'other', 'such', 'only', 'even', 'still', 'well', 'good', 'new', 'one', 'two',
  'ok', 'okay', 'yeah', 'please', 'thanks', 'thank',
  'el', 'la', 'los', 'las', 'un', 'una', 'unos', 'unas', 'de', 'del', 'al',
  'pero', 'como', 'para', 'por', 'con', 'sin', 'sobre', 'entre', 'hasta',
  'esto', 'esta', 'este', 'eso', 'ese', 'esa', 'esos', 'esas', 'que', 'mi',
  'su', 'es', 'son', 'era', 'hay', 'tiene', 'tengo', 'hacer', 'hace', 'puede',
  'quiero', 'necesito', 'hoy', 'manana', 'ahora', 'muy', 'mas', 'todo', 'todos',
  'bien', 'gracias', 'hola',
]);

// Consonant phonetic classes (voiced/unvoiced pairs collapse). Vowels + w/y/h
// drop out. Mirrors the box matcher so client + box agree on what's "close".
const CLASS = {
  b: 'P', p: 'P', f: 'F', v: 'F', d: 'T', t: 'T',
  g: 'K', k: 'K', c: 'K', q: 'K', x: 'K',
  s: 'S', z: 'S', l: 'L', r: 'L', m: 'N', n: 'N', j: 'J',
  w: '', y: '', h: '',
};

export function phoneticKey(word) {
  const w = String(word || '').toLowerCase().normalize('NFD')
    .replace(/[̀-ͯ]/g, '').replace(/[^a-z]/g, '');
  let out = '';
  for (const ch of w) {
    if ('aeiou'.includes(ch)) continue;
    const m = CLASS[ch];
    if (!m) continue;
    if (out[out.length - 1] !== m) out += m;
  }
  return out;
}

// Exported for the server-side entity-snap (agent/tools/vocabulary.js,
// TICKET-098): names with vowel-heavy phonetic keys ("Yaiza" → key "S")
// are excluded from the phonetic index by design, so the snap needs a
// direct conservative edit-distance fallback.
export function levenshtein(a, b) {
  if (a === b) return 0;
  const m = a.length, n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[n];
}

function escapeRegex(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Build the phonetic candidate index from the user's live entity names
 * (project / vessel / deal names from AppDataContext). Multi-word names are
 * split into their significant word tokens so "SY Artemis" registers "Artemis".
 * Each candidate keeps its original casing as the canonical replacement.
 *
 * @param {Array<string>} names  raw canonical names (any casing, may be multi-word)
 * @returns {Array<{ term: string, key: string, len: number }>}
 */
export function buildNameIndex(names) {
  const index = [];
  const seen = new Set();
  if (!Array.isArray(names)) return index;
  for (const raw of names) {
    const full = String(raw || '').trim();
    if (!full) continue;
    // Each significant word token becomes its own candidate. \p{L}{4,} matches
    // the same token shape the corrector scans, so single-word names register
    // as one candidate and multi-word names register per significant token.
    const tokens = full.match(/[\p{L}]{4,}/gu) || [];
    for (const tok of tokens) {
      const low = tok.toLowerCase();
      if (seen.has(low) || COMMON_WORDS.has(low)) continue;
      const key = phoneticKey(tok);
      if (key.length < 2) continue; // too few consonants to match safely
      seen.add(low);
      index.push({ term: tok, key, len: tok.length });
    }
  }
  return index;
}

/**
 * TICKET-526 — how many edits a token of this length may absorb before the
 * "correction" stops being a correction. Two edits on a 4-letter word replaces
 * half of it; that is a different word, not a mishearing.
 * The boundary sits at 4, not 5: `muruk`->`Muluc` is a REAL project-name
 * correction of distance 2 on a 5-letter token (pinned by an existing test), so
 * 5 must keep its budget of 2. At 4 characters two edits is half the word.
 * Keep in sync with services/stt-proxy/vocabCorrect.js.
 */
export function maxEditsFor(token) {
  return String(token || '').length <= 4 ? 1 : 2;
}

/**
 * Correct close phonetic mishearings of live entity names in `text`. Conservative
 * by design (auto-edits the transcript): only rewrites a token when its phonetic
 * key EQUALS a candidate's, the candidate is within maxEditsFor(token) edits and length 2,
 * and the token is neither a common word nor already the canonical name.
 *
 * @param {string} text
 * @param {Array<{ term: string, key: string, len: number }>} nameIndex  from buildNameIndex
 * @returns {{ text: string, corrections: string[] }}  corrections are "wrong~>Right" strings
 */
export function correctProperNounsSync(text, nameIndex) {
  const raw = String(text || '');
  if (!raw || !Array.isArray(nameIndex) || nameIndex.length === 0) {
    return { text: raw, corrections: [] };
  }
  let out = raw;
  const corrections = [];
  const tokens = out.match(/\b[\p{L}]{4,}\b/gu) || [];
  const seen = new Set();
  for (const tok of tokens) {
    const low = tok.toLowerCase();
    if (seen.has(low)) continue;
    seen.add(low);
    if (COMMON_WORDS.has(low)) continue;
    const tkey = phoneticKey(tok);
    if (tkey.length < 2) continue;
    for (const n of nameIndex) {
      if (n.term.toLowerCase() === low) break;       // already canonical — leave it
      if (Math.abs(n.len - tok.length) > 2) continue;
      if (n.key !== tkey) continue;
      // TICKET-526: the edit budget must scale with the word, or a SHORT token
      // gets half of itself rewritten. In production 2026-07-29 a flat budget of
      // 2 turned ordinary speech into entity names — `less~>Luis`, `nice~>Mike`,
      // `time~>timon`, `mutt~>Mont` — because on a 4-letter token two edits is
      // 50% of the word and the phonetic key alone is far too weak a signal at
      // that length. Mirrored in services/stt-proxy/vocabCorrect.js.
      if (levenshtein(low, n.term.toLowerCase()) > maxEditsFor(low)) continue;
      const re = new RegExp(`\\b${escapeRegex(tok)}\\b`, 'gi');
      out = out.replace(re, n.term);
      corrections.push(`${low}~>${n.term}`);
      break;
    }
  }
  return { text: out, corrections };
}
